import { useState, useEffect, useCallback, useRef } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Upload, Save, Clock, Eye, Edit2 } from 'lucide-react'
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  useToast,
} from '@/components/ui'
import { apiClient } from '@/lib/api'

type Platform = 'styleguide' | 'blog' | 'linkedin' | 'social_media' | 'website'

interface ContentBrief {
  id: string
  platform: Platform
  draft_text: string | null
  created_at: string
}

const PLATFORMS: { value: Platform; label: string }[] = [
  { value: 'styleguide', label: 'Allgemeiner Styleguide' },
  { value: 'blog', label: 'Blog' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'social_media', label: 'Social Media' },
  { value: 'website', label: 'Website' },
]

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(new Date(iso))
}

interface PlatformEditorProps {
  platform: Platform
}

function PlatformEditor({ platform }: PlatformEditorProps) {
  const { toast } = useToast()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [versions, setVersions] = useState<ContentBrief[]>([])
  const [draftText, setDraftText] = useState('')
  const [saving, setSaving] = useState(false)
  const [showPreview, setShowPreview] = useState(false)

  const loadVersions = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const resp = await apiClient.get<{ data: ContentBrief[] }>(
        `/marketing/content-briefs?platform=${platform}`,
      )
      const rows = resp.data.data ?? []
      setVersions(rows)
      // Pre-fill editor with most recent version
      if (rows.length > 0 && rows[0]) {
        setDraftText(rows[0].draft_text ?? '')
      }
    } catch (err: unknown) {
      setError((err as Error).message ?? 'Fehler beim Laden')
    } finally {
      setLoading(false)
    }
  }, [platform])

  useEffect(() => {
    void loadVersions()
  }, [loadVersions])

  const handleSave = async () => {
    setSaving(true)
    try {
      await apiClient.post('/marketing/content-briefs', { platform, draft_text: draftText })
      toast({ title: 'Gespeichert', tone: 'success' })
      void loadVersions()
    } catch {
      toast({ title: 'Fehler beim Speichern', tone: 'danger' })
    } finally {
      setSaving(false)
    }
  }

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (evt) => {
      const text = evt.target?.result as string
      setDraftText(text)
      toast({ title: `${file.name} importiert`, tone: 'info' })
    }
    reader.readAsText(file, 'UTF-8')
    // Reset so same file can be re-imported
    e.target.value = ''
  }

  if (loading) return <LoadingState />
  if (error) return <ErrorState title="Fehler beim Laden" description={error} />

  return (
    <div className="flex flex-col gap-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowPreview((v) => !v)}
          >
            {showPreview ? (
              <><Edit2 className="w-4 h-4 mr-1" />Bearbeiten</>
            ) : (
              <><Eye className="w-4 h-4 mr-1" />Vorschau</>
            )}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".txt,.md"
            className="hidden"
            onChange={handleImport}
          />
          <Button
            variant="secondary"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="w-4 h-4 mr-1" />
            .txt / .md importieren
          </Button>
        </div>
        <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
          <Save className="w-4 h-4 mr-1" />
          {saving ? 'Speichern…' : 'Version speichern'}
        </Button>
      </div>

      {/* Editor / Preview */}
      <Card>
        <Card.Body>
          {showPreview ? (
            <div className="prose prose-sm max-w-none min-h-[320px] p-2">
              {draftText.trim() ? (
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{draftText}</ReactMarkdown>
              ) : (
                <p className="text-gray-400 italic">Keine Inhalte zum Vorschauen.</p>
              )}
            </div>
          ) : (
            <Textarea
              className="min-h-[320px] font-mono text-sm resize-y"
              placeholder="Markdown-Inhalt eingeben…"
              value={draftText}
              onChange={(e) => setDraftText(e.target.value)}
            />
          )}
        </Card.Body>
      </Card>

      {/* Version history */}
      <div>
        <h3 className="text-h4 mb-2 flex items-center gap-1.5">
          <Clock className="w-4 h-4 text-gray-400" />
          Versionshistorie
        </h3>
        {versions.length === 0 ? (
          <EmptyState
            title="Noch keine gespeicherten Versionen"
            description={'Schreibe Inhalte in den Editor und klicke auf "Version speichern".'}
          />
        ) : (
          <div className="flex flex-col gap-1">
            {versions.map((v, idx) => (
              <button
                key={v.id}
                type="button"
                className="flex items-center justify-between rounded border border-gray-200 px-3 py-2 text-left hover:bg-gray-50 transition text-sm"
                onClick={() => setDraftText(v.draft_text ?? '')}
                title="Diese Version in den Editor laden"
              >
                <span className="text-gray-700">
                  {idx === 0 ? 'Aktuelle Version' : `Version ${versions.length - idx}`}
                </span>
                <span className="text-gray-400 text-caption">{formatDate(v.created_at)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default function ContentBriefPage() {
  const [activeTab, setActiveTab] = useState<Platform>('styleguide')

  return (
    <div className="p-6 max-w-5xl mx-auto flex flex-col gap-6">
      <PageHeader
        title="Content-Vorgaben"
        description="Lege kanal-spezifische Styleguides und Briefs fest. Die KI liest diese Vorgaben automatisch beim Erstellen von Inhalten."
        breadcrumbs={[{ label: 'Marketing' }, { label: 'Content-Vorgaben' }]}
      />

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as Platform)}>
        <TabsList>
          {PLATFORMS.map((p) => (
            <TabsTrigger key={p.value} value={p.value}>
              {p.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {PLATFORMS.map((p) => (
          <TabsContent key={p.value} value={p.value} className="mt-4">
            <PlatformEditor platform={p.value} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  )
}
